import { useTranslation } from 'react-i18next'

import { cn } from '#/shared/lib/utils'

/**
 * Tier chip for a line's provider, rendered next to the number wherever a line
 * is listed or picked. Speko lines are "Premium" (iMessage — text-only, no MMS
 * or calls); Telnyx lines are "Standard". Both tiers are labelled explicitly so
 * a missing chip reads as missing data rather than as "standard".
 *
 * `compact` shrinks the label to its initial (S / P) for tight one-line
 * contexts like a picker's trigger, where the word crowds out the number it
 * annotates. Only use it where the full word is one interaction away — the
 * open list spells it out, which is what teaches the initial. The word is
 * still on the element for assistive tech and on hover.
 */
export function LineProviderBadge({
  isImessage,
  compact,
  className,
}: {
  isImessage: boolean
  compact?: boolean
  className?: string
}) {
  const { t } = useTranslation()
  const full = t(
    isImessage
      ? 'telephony.providerBadge.premium'
      : 'telephony.providerBadge.standard',
  )
  const short = t(
    isImessage
      ? 'telephony.providerBadge.premiumShort'
      : 'telephony.providerBadge.standardShort',
  )
  return (
    <span
      className={cn(
        'shrink-0 rounded py-0.5 text-xs font-medium',
        // A single glyph needs even padding to read as a chip rather than as a
        // letter that drifted into a box.
        compact ? 'px-1' : 'px-1.25',
        isImessage
          ? 'bg-blue-500/10 text-blue-500'
          : 'bg-gray-100 text-gray-500',
        className,
      )}
      title={compact ? full : undefined}
      aria-label={compact ? full : undefined}
    >
      {compact ? short : full}
    </span>
  )
}
